import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { useBrainStore } from '@/state/brainStore';
import { useSessionStore } from '@/state/sessionStore';
import { aud } from '@/utils/format';
import {
  closeDay,
  reopenDay,
  dayPackFor,
  deferredPayoutArtists,
  expectedTillAud,
  unsignedPayoutArtists,
} from '@/domain/dayCloseFlow';
import { artistOutstanding } from '@/domain/payoutFlow';
import type { StudioId } from '@/domain/types';

/**
 * Day close (Prompt E4) — the end-of-day ritual on the money screen (per the
 * amendment that keeps money off the floor). Count the till, see the variance
 * against the E2 ledger, glance at any unsigned payouts, then close and send the
 * frozen pack to accounts. Reopening is role-gated (Operations Lead / Owner) and
 * needs a reason. All writes go through dayCloseFlow.
 */

interface Props {
  studioId: StudioId;
  studioName: string;
  dayIso: string;
}

const stamp = (iso: string): string => format(new Date(iso), 'h:mmaaa');

export default function DayClosePanel({ studioId, studioName, dayIso }: Props) {
  const cashLedgers = useBrainStore((s) => s.cashLedgers);
  const dayPacks = useBrainStore((s) => s.dayPacks);
  const bookings = useBrainStore((s) => s.bookings);
  const payoutRecords = useBrainStore((s) => s.payoutRecords);
  const artists = useBrainStore((s) => s.artists);
  const agents = useBrainStore((s) => s.agents);
  const persona = useSessionStore((s) => s.persona);
  const agentId = useSessionStore((s) => s.agentId);

  const [counted, setCounted] = useState('');
  const [reason, setReason] = useState('');

  const pack = useMemo(
    () => dayPackFor(studioId, dayIso),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [studioId, dayIso, dayPacks],
  );
  const closed = pack !== undefined && !pack.reopenedAt;
  const expected = useMemo(
    () => expectedTillAud(studioId, dayIso),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [studioId, dayIso, cashLedgers],
  );
  const unsigned = useMemo(
    () => unsignedPayoutArtists(studioId, dayIso),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [studioId, dayIso, bookings, payoutRecords],
  );
  const deferred = useMemo(
    () => deferredPayoutArtists(studioId, dayIso),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [studioId, dayIso, bookings, payoutRecords],
  );
  // Everything carried from BEFORE today, per artist per work day — the debt the
  // close has to show before anyone counts the till and goes home.
  const arrears = useMemo(
    () =>
      Object.values(artists)
        .filter((a) => a.studioId === studioId && !a.archived)
        .flatMap((a) =>
          artistOutstanding(studioId, a.id, dayIso)
            .filter((d) => d.dayIso < dayIso.slice(0, 10))
            .map((d) => ({ ...d, artistId: a.id, artistName: a.displayName })),
        )
        .sort((a, b) => a.dayIso.localeCompare(b.dayIso)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [studioId, dayIso, artists, payoutRecords],
  );

  const canReopen = persona === 'owner' || persona === 'georgina';
  const closerName = agents[agentId ?? '']?.firstName ?? (persona === 'owner' ? 'Owner' : 'Manager');

  const countedNum = Number(counted);
  const countedValid = counted.trim() !== '' && Number.isFinite(countedNum) && countedNum >= 0;
  const previewVariance = countedValid ? expected - Math.round(countedNum) : 0;

  const varianceLabel = (v: number): string =>
    v === 0 ? 'balanced' : `${aud(Math.abs(v))} ${v > 0 ? 'short' : 'over'}`;

  const doClose = () => {
    if (!countedValid) return;
    closeDay({ studioId, dayIso, countedAud: Math.round(countedNum), closedBy: closerName });
    setCounted('');
  };
  const doReopen = () => {
    if (reason.trim() === '') return;
    reopenDay({ studioId, dayIso, reopenedBy: closerName, reason: reason.trim() });
    setReason('');
  };

  return (
    <div className="panel p-4 mt-4" data-testid="day-close-panel">
      <p className="label-small-caps text-gold-bright mb-3">Close the day · {studioName}</p>

      {closed && pack ? (
        <div data-testid="day-closed-summary">
          <div className="rounded-md border border-mint/45 bg-mint/5 p-3">
            <span className="pill-mint text-[10px]">Day closed</span>
            <p className="font-serif text-cream text-sm mt-2">
              Closed by {pack.closedBy} at {stamp(pack.closedAt)} · counted {aud(pack.countedAud)} ·{' '}
              <span className={pack.varianceAud === 0 ? 'text-mint' : 'text-blood'}>
                till {varianceLabel(pack.varianceAud)}
              </span>
              . Pack sent to accounts.
            </p>
            {pack.deferredArtistIds.length > 0 ? (
              <p className="font-inter text-[11px] text-cream-soft mt-1.5" data-testid="day-closed-deferred">
                {pack.deferredArtistIds.length} envelope
                {pack.deferredArtistIds.length === 1 ? '' : 's'} deferred whole — owed on the next day the
                cash covers them, recorded on the pack.
              </p>
            ) : null}
          </div>

          {canReopen ? (
            <div className="mt-3 rounded-md border border-gold-dim/25 p-3">
              <p className="font-inter text-[10px] uppercase tracking-widest text-cream-soft">
                Reopen the day (Operations Lead / Owner)
              </p>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Reason (required — logged, never a silent edit)"
                aria-label="Reopen reason"
                className="mt-2 block w-full bg-ink-deep border border-gold-dim/40 rounded px-2 py-1 text-xs text-cream placeholder:text-cream-faint"
                data-testid="day-close-reopen-reason"
              />
              <button
                type="button"
                onClick={doReopen}
                disabled={reason.trim() === ''}
                className="btn-quiet text-[10px] px-2.5 py-1 mt-2 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="day-close-reopen"
              >
                Reopen day
              </button>
            </div>
          ) : (
            <p className="font-inter text-[11px] text-cream-faint mt-3">
              Only an Operations Lead or Owner can reopen a closed day.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {pack?.reopenedAt ? (
            <div className="rounded-md border border-gold-bright/45 bg-gold/5 p-2.5">
              <span className="pill-gold text-[10px]">Reopened</span>
              <p className="font-serif text-cream-soft text-xs mt-1.5">
                Reopened by {pack.reopenedBy} · {pack.reopenReason}. Re-close when the day is settled again.
              </p>
            </div>
          ) : null}

          {/* Step 1 — count the till. */}
          <div className="rounded-md border border-gold-dim/25 p-3">
            <p className="font-inter text-[10px] uppercase tracking-widest text-cream-soft">1 · Count the till</p>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span className="font-mono text-cream-faint text-xs">$</span>
              <input
                type="number"
                min="0"
                step="10"
                inputMode="decimal"
                value={counted}
                onChange={(e) => setCounted(e.target.value)}
                placeholder="0"
                aria-label="Cash counted"
                className="w-28 bg-ink-deep border border-gold-dim/40 rounded px-2 py-1 text-right font-mono text-xs text-cream"
                data-testid="day-close-counted"
              />
              <span className="font-inter text-[11px] text-cream-soft">
                Expected {aud(expected)}
                {countedValid ? (
                  <>
                    {' · '}
                    <span className={previewVariance === 0 ? 'text-mint' : 'text-blood'}>
                      {varianceLabel(previewVariance)}
                    </span>
                  </>
                ) : null}
              </span>
            </div>
          </div>

          {/* Step 2 — payouts check. An envelope the day could not cover is a
              logged decision (deferred, owed whole tomorrow); an unsigned one is
              an oversight the close is about to sweep into a deferral. They read
              separately, because they are not the same thing. */}
          <div className="rounded-md border border-gold-dim/25 p-3">
            <p className="font-inter text-[10px] uppercase tracking-widest text-cream-soft">2 · Payouts</p>
            {unsigned.length === 0 ? (
              <p className="font-serif text-mint text-sm mt-1.5">All payable envelopes are signed.</p>
            ) : (
              <p className="font-serif text-cream-soft text-sm mt-1.5" data-testid="day-close-unsigned">
                {unsigned.length} still unsigned: {unsigned.map((p) => p.artistName).join(', ')}. You can
                still close — closing rolls each one whole to the next day the cash covers it, and records
                it on the pack. Nobody is part-paid.
              </p>
            )}
            {deferred.length > 0 ? (
              <p className="font-serif text-blood text-sm mt-1.5" data-testid="day-close-deferred">
                {deferred.length} deferred today: {deferred.map((p) => p.artistName).join(', ')} · owed{' '}
                {aud(deferred.reduce((sum, p) => sum + p.envelopeAud, 0))} whole.
              </p>
            ) : null}
            {arrears.length > 0 ? (
              <div className="mt-2.5 rounded-md border border-blood/40 bg-blood/5 p-2.5" data-testid="day-close-arrears">
                <p className="font-inter text-[10px] uppercase tracking-widest text-blood">
                  Owed from earlier days
                </p>
                <ul className="mt-1.5 space-y-1">
                  {arrears.map((a) => (
                    <li
                      key={`${a.artistId}-${a.dayIso}`}
                      className="flex items-baseline justify-between gap-3 font-mono text-xs text-cream-soft"
                      data-testid={`day-close-arrears-${a.artistId}-${a.dayIso}`}
                    >
                      <span>
                        {a.artistName} · {format(new Date(`${a.dayIso}T12:00:00+10:00`), 'EEE d MMM')}
                        <span className={a.escalated ? 'text-blood ml-2' : 'text-cream-faint ml-2'}>
                          owed {a.ageDays} day{a.ageDays === 1 ? '' : 's'}
                        </span>
                      </span>
                      <span className="text-cream">{aud(a.envelopeAud)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>

          {/* Step 3 — close. */}
          <button
            type="button"
            onClick={doClose}
            disabled={!countedValid}
            className="btn-primary text-[11px] disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="day-close-confirm"
          >
            Close day &amp; send pack
          </button>
        </div>
      )}
    </div>
  );
}
